import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Loader2 } from 'lucide-react';
import { useClient } from '../../contexts/ClientContext';
import { getApiUrl } from '../../utils/config';
import '@google/model-viewer';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        src?: string;
        alt?: string;
        ar?: boolean;
        'ar-modes'?: string;
        'camera-controls'?: boolean;
        'shadow-intensity'?: string;
        'auto-rotate'?: boolean;
        loading?: string;
        style?: React.CSSProperties;
      };
    }
  }
}

interface ThreeDViewerProps {
  modelUrl: string;
  mediaId?: string;
  onClose?: () => void;
  className?: string;
  showControls?: boolean;
}

export const ThreeDViewer: React.FC<ThreeDViewerProps> = ({ 
  modelUrl, 
  mediaId,
  onClose, 
  className = "w-full h-96",
  showControls = true
}) => {
  const { currentClient } = useClient();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use centralized API URL configuration
  const getModelApiUrl = () => {
    return getApiUrl();
  };
  
  const proxyModelUrl = mediaId 
    ? `${getModelApiUrl()}/api/v1/media/${mediaId}/3d-model`
    : modelUrl;

  const downloadModel = async () => {
    try {
      setIsDownloading(true);
      console.log('Downloading 3D model from:', proxyModelUrl);
      
      const response = await fetch(proxyModelUrl, {
        headers: currentClient ? {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        } : {},
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'model.glb';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('Download completed successfully');
    } catch (error) {
      console.error('Error downloading model:', error);
      alert('Failed to download 3D model. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleModelLoad = () => {
    setIsLoading(false);
    setError(null);
  };

  const handleModelError = () => {
    setIsLoading(false);
    setError('Failed to load 3D model');
  };

  return (
    <div className="space-y-4">
      <div className={`${className} relative bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700`}>
        {error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">Failed to Load 3D Model</h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4 max-w-md">
              The 3D model couldn't be displayed. You can still download it to view in other applications.
            </p>
            {showControls && (
              <Button 
                onClick={downloadModel} 
                disabled={isDownloading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2"
              >
                <Download className="h-4 w-4 mr-2" />
                {isDownloading ? 'Downloading...' : 'Download Model'}
              </Button>
            )}
          </div>
        ) : (
          <>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-50/80 dark:bg-slate-800/80 z-10">
                <div className="flex flex-col items-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-2" />
                  <p className="text-sm text-slate-600 dark:text-slate-400">Loading 3D model...</p>
                </div>
              </div>
            )}
            
            <model-viewer
              src={proxyModelUrl}
              alt="3D Model"
              camera-controls={showControls}
              auto-rotate={showControls}
              shadow-intensity="1"
              loading="eager"
              style={{ 
                width: '100%', 
                height: '100%',
                backgroundColor: 'transparent'
              }}
              ref={(el: any) => {
                if (el) {
                  // Remove existing listeners to avoid duplicates
                  el.removeEventListener('load', handleModelLoad);
                  el.removeEventListener('error', handleModelError);
                  
                  // Add proper event listeners for model-viewer web component
                  el.addEventListener('load', handleModelLoad);
                  el.addEventListener('error', handleModelError);
                }
              }}
            />
          </>
        )}

        {onClose && (
          <Button
            variant="outline"
            size="sm"
            className="absolute top-2 right-2 bg-white/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-800 z-20"
            onClick={onClose}
          >
            ✕
          </Button>
        )}

        {showControls && !error && (
          <div className="absolute bottom-2 right-2 z-20">
            <Button 
              size="sm"
              variant="outline"
              onClick={downloadModel} 
              disabled={isDownloading}
              className="bg-white/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-800"
            >
              <Download className="h-3 w-3 mr-1" />
              {isDownloading ? 'Downloading...' : 'Download'}
            </Button>
          </div>
        )}
      </div>

      {showControls && (
        <div className="text-center text-xs text-slate-500 dark:text-slate-400">
          <p>💡 <strong>Tip:</strong> Click and drag to rotate • Scroll to zoom • Right-click and drag to pan</p>
        </div>
      )}
    </div>
  );
};